import { useEffect, useState, FormEvent } from 'react'
import { createApi } from 'unsplash-js'

import { SelectedImage, SetState } from 'types'
import { reportError } from 'utils/error'
import { REACT_APP_UNSPLASH_API_KEY } from 'env'

import Preview from './Preview'
import { Basic } from 'unsplash-js/dist/methods/photos/types'
import { Loader } from 'components/Loader/Loader'
import { SearchForm } from './SearchForm'

export interface Props {
  stagedImages: SelectedImage[]
  selectedImages: SelectedImage[]
  setSelectedImages: SetState<SelectedImage[]>
  setStagedImages: SetState<SelectedImage[]>
  multiSelect?: boolean
  uploadHighRes?: boolean
  initialQuery?: string
  showSearchIcon?: boolean
}

const unsplashApi = createApi({
  accessKey: REACT_APP_UNSPLASH_API_KEY,
})

const UploadFromUnsplash = ({
  selectedImages,
  stagedImages,
  setSelectedImages,
  setStagedImages,
  multiSelect,
  uploadHighRes,
  initialQuery = '',
  showSearchIcon = false,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(1)
  const [maxPage, setMaxPage] = useState(1)
  const [querySubmitted, setQuerySubmitted] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [query, setQuery] = useState(initialQuery)

  const mapResponseToImages = (response: Basic[]) => {
    return response.map((item: Basic) => {
      const url = uploadHighRes ? item.urls.full : item.urls.regular
      return {
        url,
        unsplash: {
          authorUsername: item.user.username,
          authorName: item.user.name,
          id: item.id,
          query,
        },
      }
    })
  }

  const getPhotos = async (nextPage?: number) => {
    setCanLoadMore(false)

    const page = nextPage || currentPage

    try {
      const request = await unsplashApi.search.getPhotos({ query, page })
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const images = mapResponseToImages(request?.response?.results!)
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      setMaxPage(request.response?.total_pages!)

      return images
    } catch (error) {
      reportError(error, { severity: 'error' })
    }
  }

  useEffect(() => {
    if (stagedImages.length > 0) return

    if (query) {
      search()
      return
    }

    const loadRandomPhotos = async () => {
      try {
        const request = await unsplashApi.photos.getRandom({ count: 10 })
        if (request && request.response) {
          const images = mapResponseToImages(request.response as unknown as Basic[])
          setStagedImages(images)
        }
      } catch (error) {
        reportError(error, { severity: 'error' })
      }
    }

    loadRandomPhotos()
  })

  const search = async () => {
    setQuerySubmitted(true)

    const photos = await getPhotos()
    if (!photos) return

    setStagedImages(photos)
    setCanLoadMore(true)
  }

  const onSubmit = async (e?: FormEvent | KeyboardEvent) => {
    e?.preventDefault()
    if (query.length < 3) return
    search()
  }

  const clearQuery = () => {
    setQuery('')
    setStagedImages([])
    setSelectedImages([])
    setQuerySubmitted(false)
  }

  const loadMore = async () => {
    if (!canLoadMore) return

    setCanLoadMore(false)
    const nextPage = currentPage + 1
    setCurrentPage(nextPage)

    const photos = await getPhotos(nextPage)
    setStagedImages((oldState) => [...oldState, ...(photos || [])])
  }

  useEffect(() => {
    if (canLoadMore || !querySubmitted) return

    const timer = setTimeout(() => {
      setCanLoadMore(true)
    }, 2500)

    return () => clearTimeout(timer)
  }, [canLoadMore, querySubmitted])

  return (
    <>
      <SearchForm
        placeholder="Search Unsplash for photos"
        query={query}
        clearQuery={clearQuery}
        onChange={(e) => setQuery(e.target.value)}
        onSubmit={onSubmit}
        showSearchAffordance
        showIcon={showSearchIcon}
      />

      {stagedImages.length ? (
        <Preview
          stagedImages={stagedImages}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          loadMore={loadMore}
          hasMore={canLoadMore && currentPage <= maxPage}
          multiSelect={multiSelect}
        />
      ) : (
        <Loader />
      )}
    </>
  )
}

export default UploadFromUnsplash
